import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faCheck,
  faHourglassStart,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { isFuture } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  ReferralRewardState,
  ReferralState,
  ReferralState as ReferralStateEnum,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  BodyL,
  BodyM,
  HeadlineL,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ClaimReferralRewardsDialog } from '@sorare/core/src/components/referral/ClaimReferralRewardsDialog';
import { Progression } from '@sorare/core/src/components/referral/Progression';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { TERMS_AND_CONDITIONS } from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useDestroyReferral } from '@sorare/core/src/hooks/referral/useDestroyReferral';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import {
  Sum,
  useMonetaryAmount,
} from '@sorare/core/src/hooks/useMonetaryAmount';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { glossary, referralStateMessages } from '@sorare/core/src/lib/glossary';

import {
  Body,
  Placeholder,
  ResponsiveNumber,
  RightBlock,
} from '#src/components/invite/OnlineLanding/RightBlock/index.tsx';

import type {
  ReferralItem_referral,
  ReferralListQuery,
  ReferralListQueryVariables,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Referral hub',
  },
  subtitle: {
    defaultMessage: 'Track your friends progression',
  },
  progression: {
    defaultMessage:
      '{completed, plural, one {<b>#</b> Successful Referral} other {<b>#</b> Successful Referrals}} out of {total, plural, one {<b>#</b> Invited} other {<b>#</b> Invited}}',
  },
  pendingReview: {
    defaultMessage: 'This referral is under review.',
  },
  pendingReviewDescription: {
    defaultMessage:
      'We are reviewing this referral for authenticity and checking if it follows our <link>Terms and Conditions</link> before rewarding you. This process takes up to 1 week.',
  },
  pendingReferreeConfirmation: {
    defaultMessage: 'This referral is pending confirmation.',
  },
  pendingReferreeConfirmationDescription: {
    defaultMessage:
      'We are waiting for your friend to confirm their phone number.',
  },
  blocked: {
    defaultMessage: 'This referral is suspended.',
  },
  blockedDescription: {
    defaultMessage: 'This referee has been reviewed as suspicious.',
  },
});

const ReferralList = styled(Vertical).attrs({ gap: 2 })`
  align-self: stretch;
`;

const StyledResponsiveNumber = styled(ResponsiveNumber)`
  max-height: 380px;
`;

const StyledBody = styled(Body)`
  padding: 0 var(--quadruple-unit) var(--quadruple-unit) var(--quadruple-unit);

  & > * {
    width: 100%;
  }
`;

const ReferralStateIcon = ({ state }: { state: ReferralState }) => {
  switch (state) {
    case ReferralState.EXPIRED:
      return <FontAwesomeIcon color="var(--c-red-500)" icon={faXmark} />;
    case ReferralState.COMPLETED:
      return <FontAwesomeIcon color="var(--c-onBase-success)" icon={faCheck} />;
    default:
      return (
        <FontAwesomeIcon color="var(--c-blue-300)" icon={faHourglassStart} />
      );
  }
};

const ProgressContainer = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  --active-color: var(--c-onBase-success);
`;

const Header = styled(SBHorizontal)`
  margin-bottom: var(--unit);
`;

const ExpiredReferralStatusAction = ({ id }: { id: string }) => {
  const destroyReferral = useDestroyReferral();

  return (
    <Button
      color="red"
      size="small"
      onClick={() => {
        destroyReferral(id);
      }}
    >
      <FormattedMessage {...glossary.delete} />
    </Button>
  );
};

const ProgressionBar = ({
  cardsBought,
  cardsRequirements,
}: {
  cardsBought: number;
  cardsRequirements: number;
}) => {
  return (
    <ProgressContainer>
      <>
        <Header>
          <LabelS as="p">
            <FormattedMessage defaultMessage="Cards collected" />
          </LabelS>
          <LabelS as="p">
            {Math.min(cardsBought, cardsRequirements)}/{cardsRequirements}
          </LabelS>
        </Header>
        <Progression
          hideCount
          count={cardsBought}
          max={cardsRequirements || undefined}
        />
      </>
    </ProgressContainer>
  );
};

const ReferralStatus = ({
  referral,
  cardsRequirements,
  referralProgramConfigAmount,
}: {
  referral: ReferralItem_referral;
  cardsRequirements: number;
  referralProgramConfigAmount: string;
}) => {
  const cardsBought = referral.refereeSportCardsBoughtFromPrimaryMarketCount;
  const { toMonetaryAmount } = useMonetaryAmount();
  const conversionCreditsWon = Sum(
    referral.referrerRewards
      .map(referrerReward => referrerReward.conversionCredit?.maxDiscount)
      .filter(Boolean)
      .map(cc => toMonetaryAmount(cc))
  );

  const { main } = useAmountWithConversion({
    monetaryAmount: conversionCreditsWon,
  });

  if (referral.state === ReferralState.EXPIRED) {
    return <ExpiredReferralStatusAction id={referral.id} />;
  }

  const progression = (
    <ProgressionBar
      cardsBought={cardsBought}
      cardsRequirements={cardsRequirements}
    />
  );

  if (referral.state === ReferralState.COMPLETED) {
    const hasWonConversionCredit = conversionCreditsWon.isPositive;
    const hasWonCard = !!referral.referrerRewards.find(
      referrerReward => !!referrerReward.anyCard
    );
    return (
      <Vertical gap={4} center>
        {progression}
        {referral.referrerRewards.length > 0 && (
          <>
            {hasWonConversionCredit && hasWonCard ? (
              <BodyM color="var(--c-nd-600)">
                <FormattedMessage
                  defaultMessage="Congratulations! You won {amount} and a Card"
                  values={{
                    amount: main,
                  }}
                />
              </BodyM>
            ) : (
              <>
                {!!hasWonConversionCredit && (
                  <BodyM color="var(--c-nd-600)">
                    <FormattedMessage
                      defaultMessage="Congratulations! You won {amount}"
                      values={{
                        amount: main,
                      }}
                    />
                  </BodyM>
                )}
                {!!hasWonCard && (
                  <BodyM color="var(--c-nd-600)">
                    <FormattedMessage defaultMessage="Congratulations! You won a card" />
                  </BodyM>
                )}
              </>
            )}
          </>
        )}
      </Vertical>
    );
  }

  if (referral.state === ReferralState.PENDING_REVIEW) {
    return (
      <Vertical gap={4}>
        {progression}
        <Warning
          variant="yellow"
          title={messages.pendingReview}
          message={messages.pendingReviewDescription}
          values={{
            link: content => <Link to={TERMS_AND_CONDITIONS}>{content}</Link>,
          }}
        />
      </Vertical>
    );
  }

  if (referral.state === ReferralState.PENDING_REFEREE_CONFIRMATION) {
    return (
      <Vertical gap={4}>
        {progression}
        <Warning
          variant="yellow"
          title={messages.pendingReferreeConfirmation}
          message={messages.pendingReferreeConfirmationDescription}
        />
      </Vertical>
    );
  }

  if (referral.state === ReferralState.BLOCKED) {
    return (
      <Vertical gap={4}>
        {progression}
        <Warning
          variant="yellow"
          title={messages.blocked}
          message={messages.blockedDescription}
        />
      </Vertical>
    );
  }

  return (
    <Vertical gap={4}>
      {progression}
      <BodyM color="var(--c-nd-600)">
        <FormattedMessage
          defaultMessage="Only {count, plural, one {# Card} other {# Cards}} to go to receive up to {amount} credits!"
          values={{
            count: Math.max(cardsRequirements - cardsBought, 0),
            amount: referralProgramConfigAmount,
          }}
        />
      </BodyM>
    </Vertical>
  );
};

const StyledButtonBase = styled(ButtonBase)`
  text-align: left;
`;

const StyledTabBar = styled(TabBar)`
  --bar-bg-color-custom: 'transparent';
`;

const ReferralItem = ({
  referral,
  referralProgramConfigAmount,
  cardsRequirements,
}: {
  referral: ReferralItem_referral;
  referralProgramConfigAmount: string;
  cardsRequirements: number;
}) => {
  const [openRefereeDetails, toggleOpenRefereeDetails] = useToggle(false);
  const [openClaimDialog, toggleOpenClaimDialog] = useToggle(false);

  const hasClaimableRewards = referral.referrerRewards.some(
    reward => reward.claimableState === ReferralRewardState.CLAIMABLE
  );
  const allClaimed =
    referral.referrerRewards.length > 0 &&
    referral.referrerRewards.every(
      reward => reward.claimableState === ReferralRewardState.CLAIMED
    );
  const someUnclaimable =
    referral.referrerRewards.length > 0 &&
    referral.referrerRewards.every(reward =>
      [
        ReferralRewardState.NOT_CLAIMABLE_YET,
        ReferralRewardState.NOT_CLAIMABLE_YET_1_GW_MISSING,
        ReferralRewardState.NOT_CLAIMABLE_YET_2_GW_MISSING,
      ].includesValue(reward.claimableState)
    );
  const claimableAt = referral.referrerRewards
    .map(r => r.claimableAt)
    .filter(Boolean)
    .sort()[0];

  return (
    <>
      <SBHorizontal key={referral.id}>
        <StyledButtonBase onClick={toggleOpenRefereeDetails}>
          <Horizontal>
            <Avatar user={referral.referee} rounded size={2} />
            <Vertical grow gap={0}>
              <UserName user={referral.referee} />
              <LabelM color="var(--c-nd-600)">
                <FormattedMessage {...referralStateMessages[referral.state]} />
              </LabelM>
            </Vertical>
          </Horizontal>
        </StyledButtonBase>
        {(() => {
          if (allClaimed) {
            return (
              <span>
                <FormattedMessage {...glossary.claimed} />{' '}
                <FontAwesomeIcon
                  icon={faCheck}
                  color="var(--c-onBase-success)"
                />
              </span>
            );
          }
          if (hasClaimableRewards) {
            return (
              <Button
                key={referral.id}
                size="small"
                color="primary"
                onClick={toggleOpenClaimDialog}
              >
                <FormattedMessage {...glossary.claim} />
              </Button>
            );
          }
          if (someUnclaimable) {
            return (
              <>
                {isFuture(claimableAt) && (
                  <LabelS className="text-center">
                    <FormattedMessage
                      defaultMessage="Claimable in{br}{timeLeft}"
                      values={{
                        timeLeft: <TimeLeft time={claimableAt} />,
                        br: <br />,
                      }}
                    />
                  </LabelS>
                )}
                <Button key={referral.id} size="small" color="primary" disabled>
                  <FormattedMessage {...glossary.claim} />
                </Button>
              </>
            );
          }
          return <ChevronRightBold />;
        })()}
      </SBHorizontal>
      <ClaimReferralRewardsDialog
        referralRewards={referral.referrerRewards}
        open={openClaimDialog}
        onClose={toggleOpenClaimDialog}
      />
      <Dialog
        maxWidth="sm"
        open={openRefereeDetails}
        onClose={toggleOpenRefereeDetails}
      >
        <StyledBody center>
          <Avatar size={10} user={referral.referee} rounded />
          <Vertical center>
            <LabelL color="var(--c-nd-600)">
              <Horizontal center>
                <ReferralStateIcon state={referral.state} />
                <FormattedMessage {...referralStateMessages[referral.state]} />
              </Horizontal>
            </LabelL>
            <HeadlineL>
              <UserName user={referral.referee} />
            </HeadlineL>
          </Vertical>
          <ReferralStatus
            referral={referral}
            cardsRequirements={cardsRequirements}
            referralProgramConfigAmount={referralProgramConfigAmount}
          />
        </StyledBody>
      </Dialog>
    </>
  );
};

ReferralItem.fragments = {
  referral: gql`
    fragment ReferralItem_referral on Referral {
      id
      state
      referee {
        slug
        ...UserName_publicUserInfoInterface
        ...Avatar_user
      }
      refereeSportCardsBoughtFromPrimaryMarketCount(sport: $sport)
      referrerRewards {
        id
        claimableState
        claimableAt
        anyCard {
          slug
        }
        conversionCredit {
          id
          maxDiscount {
            ...useMonetaryAmount_monetaryAmount
          }
        }
        ...ClaimReferralRewardsDialog_referralReward
      }
    }
    ${UserName.fragments.user}
    ${Avatar.fragments.user}
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${ClaimReferralRewardsDialog.fragments.referralReward}
  ` as TypedDocumentNode<ReferralItem_referral>,
};

const REFERRAL_LIST_QUERY = gql`
  query ReferralListQuery($page: Int, $sport: Sport!, $state: ReferralState) {
    currentUser {
      slug
      referrals(sport: $sport, pageSize: 10, page: $page, state: $state) {
        nodes {
          id
          ...ReferralItem_referral
        }
        currentPage
        pages
        totalCount
      }
    }
  }
  ${ReferralItem.fragments.referral}
` as TypedDocumentNode<ReferralListQuery, ReferralListQueryVariables>;

const ReferralsList = ({
  sport,
  referralProgramConfigAmount,
  cardsRequirements,
}: {
  sport: Sport;
  referralProgramConfigAmount: string;
  cardsRequirements: number;
}) => {
  const [referralState, setReferralState] = useState<ReferralStateEnum>(
    ReferralStateEnum.ALL
  );
  const { formatMessage } = useIntlContext();

  const selectOptions = [
    ReferralStateEnum.ALL,
    ReferralStateEnum.IN_PROGRESS,
    ReferralStateEnum.COMPLETED,
    ReferralStateEnum.EXPIRED,
  ].map(element => ({
    value: element,
    label: formatMessage(referralStateMessages[element]),
  }));

  const { data, loadMore, loading } = usePaginatedQuery(REFERRAL_LIST_QUERY, {
    variables: { sport, state: referralState },
    connection: 'ReferralPaginated',
  });

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        sport,
        state: referralState,
        page: (data?.currentUser?.referrals.currentPage || 0) + 1,
      });
    },
    (data?.currentUser?.referrals.currentPage || 0) <
      (data?.currentUser?.referrals.pages || 1) - 1,
    loading
  );

  return (
    <Vertical gap={3}>
      <StyledTabBar
        variant="flat"
        value={referralState}
        fullWidth
        forceMobileLayout
      >
        {selectOptions.map(s => (
          <Tab
            key={s.value}
            value={s.value}
            onClick={() => setReferralState(s.value)}
          >
            {s.label}
          </Tab>
        ))}
      </StyledTabBar>
      <ReferralList>
        {data?.currentUser?.referrals.nodes.map(referral => {
          return (
            <ReferralItem
              key={referral.id}
              referral={referral}
              referralProgramConfigAmount={referralProgramConfigAmount}
              cardsRequirements={cardsRequirements}
            />
          );
        })}
        <InfiniteScrollLoader small />
      </ReferralList>
    </Vertical>
  );
};

export const Hub = ({
  total,
  completed,
  sport,
  referralProgramConfigAmount,
  cardsRequirements,
  unclaimedReferralRewardsCount,
}: {
  total: number;
  completed: number;
  sport: Sport;
  referralProgramConfigAmount: string;
  cardsRequirements: number;
  unclaimedReferralRewardsCount: number;
}) => {
  const loading = typeof total === 'undefined';

  return (
    <RightBlock
      title={<FormattedMessage defaultMessage="Referrals" />}
      subTitle={<FormattedMessage {...messages.subtitle} />}
      total={total > 0 ? `${completed}/${total}` : `0`}
      badge={unclaimedReferralRewardsCount}
      dialogProps={{
        title: <FormattedMessage {...messages.title} />,
        subtitle: (
          <Vertical gap={3} center>
            <FormattedMessage {...messages.subtitle} />
            <BodyL color="var(--c-white)">
              <FormattedMessage
                {...messages.progression}
                values={{ completed, total, b: Bold }}
              />
            </BodyL>
          </Vertical>
        ),
        children:
          total && total > 0 ? (
            <ReferralsList
              sport={sport}
              referralProgramConfigAmount={referralProgramConfigAmount}
              cardsRequirements={cardsRequirements}
            />
          ) : (
            <Placeholder>
              <StyledResponsiveNumber total="0" alignmentBaseline="middle" />
            </Placeholder>
          ),
      }}
      loading={loading}
    />
  );
};
